/**
 * SimplePieChart - Subscription distribution pie chart using Recharts.
 */

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

interface SubscriptionBreakdown {
  free: number;
  family: number;
  classroom: number;
}

interface SimplePieChartProps {
  data: SubscriptionBreakdown;
  loading?: boolean;
  title?: string;
  onSegmentClick?: (tier: keyof SubscriptionBreakdown) => void;
}

const COLORS = {
  free: "#94a3b8", // gray-400
  family: "#8b5cf6", // purple-500
  classroom: "#10b981", // emerald-500
};

const LABELS = {
  free: "Free",
  family: "Family",
  classroom: "Classroom",
};

export function SimplePieChart({
  data,
  loading = false,
  title = "Subscription Distribution",
  onSegmentClick,
}: SimplePieChartProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-6 w-40 bg-gray-200 rounded mb-4 animate-pulse" />
        <div className="h-64 flex items-center justify-center">
          <div className="w-48 h-48 rounded-full bg-gray-100 animate-pulse" />
        </div>
      </div>
    );
  }

  // Transform data for Recharts
  const chartData = [
    { name: LABELS.free, value: data.free, key: "free" as const },
    { name: LABELS.family, value: data.family, key: "family" as const },
    { name: LABELS.classroom, value: data.classroom, key: "classroom" as const },
  ].filter((item) => item.value > 0);

  const total = data.free + data.family + data.classroom;

  const handleClick = (entry: { key: keyof SubscriptionBreakdown }) => {
    if (onSegmentClick) {
      onSegmentClick(entry.key);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="h-64">
        {total === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            No subscription data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                onClick={handleClick}
                style={{ cursor: onSegmentClick ? "pointer" : "default" }}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={entry.key}
                    fill={COLORS[entry.key]}
                    stroke="white"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                }}
                formatter={(value, name) => {
                  const numValue = typeof value === "number" ? value : 0;
                  return [
                    `${numValue} (${((numValue / total) * 100).toFixed(1)}%)`,
                    name,
                  ];
                }}
              />
              <Legend
                formatter={(value) => (
                  <span className="text-sm text-gray-700">{value}</span>
                )}
                iconType="circle"
                iconSize={8}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
      {/* Legend with counts */}
      <div className="mt-4 flex justify-center gap-6 text-sm">
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: COLORS.free }}
          />
          <span className="text-gray-600">
            Free: <span className="font-medium">{data.free}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: COLORS.family }}
          />
          <span className="text-gray-600">
            Family: <span className="font-medium">{data.family}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: COLORS.classroom }}
          />
          <span className="text-gray-600">
            Classroom: <span className="font-medium">{data.classroom}</span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default SimplePieChart;
